import { lazy, Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import {
  useRechargeActivityList,
  usePopupTimings,
  useIsHomeDisplayReady,
  useActivities,
  isActivityIdValid,
  useIsActivityQualificationAutoEnabled,
} from "../../../modules/homeDisplay/hooks";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { ROUTES } from "@/types/routes";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  ActivityItem,
  ActivityType,
  TimingType,
} from "../../../modules/homeDisplay/types";
import type { RechargePromotionPopupResult } from "./useRechargePromotionPopup.types";

const CLAIMABLE_POPUP_KEY_PREFIX = "recharge-promotion-claimable";

const getClaimablePopupKey = (userId: string, activityId: string): string => {
  return `${CLAIMABLE_POPUP_KEY_PREFIX}_${userId}_${activityId}`;
};

const hasShownClaimablePopupToday = (
  userId: string,
  activityId: string
): boolean => {
  try {
    const records = localStorage.getItem("shown_popup_records");
    if (!records) return false;
    const parsed = JSON.parse(records) as Record<
      string,
      { time: string; timestamp: number }
    >;
    const key = getClaimablePopupKey(userId, activityId);
    if (!parsed[key]) return false;

    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return parsed[key].time === todayString;
  } catch {
    return false;
  }
};

const markClaimablePopupShownToday = (
  userId: string,
  activityId: string
): void => {
  try {
    const records = localStorage.getItem("shown_popup_records");
    const parsed = records
      ? (JSON.parse(records) as Record<
          string,
          { time: string; timestamp: number }
        >)
      : {};
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const key = getClaimablePopupKey(userId, activityId);
    parsed[key] = { time: todayString, timestamp: Date.now() };
    localStorage.setItem("shown_popup_records", JSON.stringify(parsed));
  } catch {
    // ignore
  }
};

const RECHARGE_PROMOTION_DEFAULT_PRIORITY =
  POPUP_CONFIGS.RECHARGE_PROMOTION.priority;

const RechargePromotion = lazy(() =>
  import("../components/RechargePromotion/RechargePromotion").then(m => ({
    default: m.default,
  }))
);

const isRecordClaimableByRule = (record: any, banner: any): boolean => {
  const status = String(record?.status || "").toUpperCase();
  const isPending = status === "PENDING";
  if (!isPending) return false;
  const batchBasis = String(record?.batchDistributionBasis ?? "").toUpperCase();
  const isBetAmount = batchBasis === "BET_AMOUNT";
  const isDaily = batchBasis === "DAILY";
  const userCumulativeBetAmount = Number(
    ((banner as any)?.userProgress?.cumulativeBetAmount as any) ?? 0
  );
  const recordBetAmount =
    Number((record as any)?.betRatio?.betAmount ?? NaN) ||
    Number((record as any)?.betAmount ?? NaN) ||
    0;
  const effectiveDate = Number(
    ((record as any)?.dayRatio?.effectiveDate as any) ?? NaN
  );
  const nowMs = Date.now();
  const dailyReached =
    isDaily &&
    Number.isFinite(effectiveDate) &&
    effectiveDate > 0 &&
    effectiveDate <= nowMs;
  const betReached =
    isBetAmount &&
    recordBetAmount >= 0 &&
    userCumulativeBetAmount >= recordBetAmount;
  return (
    (isDaily && dailyReached) ||
    (isBetAmount && betReached) ||
    (!isDaily && !isBetAmount)
  );
};

const hasClaimableUnclaimedInternal = (banner: any): boolean => {
  const records = (((banner as any) || {})?.claimRecords || []) as any[];
  if (!Array.isArray(records) || records.length === 0) return false;
  return records.some(r => isRecordClaimableByRule(r, banner));
};

const isAllClaimedInternal = (banner: any): boolean => {
  const records = (((banner as any) || {})?.claimRecords || []) as any[];
  if (!Array.isArray(records) || records.length === 0) return false;
  return records.every(
    r => String((r as any)?.status || "").toUpperCase() === "CLAIMED"
  );
};

interface PopupRegistrationContext {
  banner: ActivityItem;
  priority: number;
  isConfigReady: boolean;
  isAuthenticated: boolean;
  userInfo: any;
  manualBannerRef: React.MutableRefObject<ActivityItem | null>;
  onRegisterTrigger: (activityId: string, trigger: () => void) => void;
  activities: any;
}

// 单个活动弹窗注册组件
const ActivityPopupRegistration: React.FC<PopupRegistrationContext> = ({
  banner,
  priority,
  isConfigReady,
  isAuthenticated,
  userInfo,
  manualBannerRef,
  onRegisterTrigger,
  activities,
}) => {
  const activityId = banner.activityGroupId || banner.id || "";
  const popupId = `recharge-promotion-auto-${banner.id}`;
  // 检查活动是否真实存在于 activities 配置中
  const isActivityValid = useMemo(() => {
    return isActivityIdValid(
      banner.activityGroupId,
      ActivityType.RECHARGE_ACTIVITY,
      activities
    );
  }, [banner.activityGroupId, activities]);
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.RECHARGE_ACTIVITY,
    activityId
  );

  const { trigger } = useQueuedPopup({
    popupId,
    priority,
    shouldShow: () =>
      isConfigReady && isAuthenticated && !!banner && isActivityValid,
    renderContent: onComplete => {
      const handleClose = () => {
        manualBannerRef.current = null;
        onComplete();
      };

      const displayBanner =
        manualBannerRef.current?.id === banner.id
          ? manualBannerRef.current
          : banner;

      return (
        <Suspense fallback={null}>
          <RechargePromotion
            visible={true}
            data={displayBanner as ActivityItem}
            onClose={handleClose}
          />
        </Suspense>
      );
    },
    showOnce: true,
    allowManualTrigger: true,
    autoTrigger: isAutoEnabled,
    dependencies: [isConfigReady, banner, isAuthenticated, isActivityValid, isAutoEnabled],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/", ROUTES.LOGIN, ROUTES.REGISTER],
    blockedPaths: [
      "/game",
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  // 注册trigger到父组件
  useEffect(() => {
    onRegisterTrigger(activityId, trigger);
  }, [activityId, trigger, onRegisterTrigger]);

  return null;
};

// 所有活动弹窗注册容器组件
const AllActivityPopupsRegistration: React.FC<{
  banners: ActivityItem[];
  getActivityPriority: (banner: ActivityItem | undefined) => number;
  isConfigReady: boolean;
  isAuthenticated: boolean;
  userInfo: any;
  manualBannerRef: React.MutableRefObject<ActivityItem | null>;
  onRegisterTrigger: (activityId: string, trigger: () => void) => void;
  activities: any;
}> = ({
  banners,
  getActivityPriority,
  isConfigReady,
  isAuthenticated,
  userInfo,
  manualBannerRef,
  onRegisterTrigger,
  activities,
}) => {
  return (
    <>
      {banners.map(banner => {
        const key = banner.id || "";
        return (
          <ActivityPopupRegistration
            key={key}
            banner={banner}
            priority={getActivityPriority(banner)}
            isConfigReady={isConfigReady}
            isAuthenticated={isAuthenticated}
            userInfo={userInfo}
            manualBannerRef={manualBannerRef}
            onRegisterTrigger={onRegisterTrigger}
            activities={activities}
          />
        );
      })}
    </>
  );
};

export const useRechargePromotionPopup = (): RechargePromotionPopupResult & {
  PopupRegistrationComponent: React.FC;
} => {
  const rechargeActivityList = useRechargeActivityList();
  const popupTimings = usePopupTimings();
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const activities = useActivities();

  const manualBannerRef = useRef<ActivityItem | null>(null);

  const getActivityPriority = useCallback(
    (banner: ActivityItem | undefined): number => {
      if (!banner?.activityGroupId) return RECHARGE_PROMOTION_DEFAULT_PRIORITY;
      const timings = popupTimings.filter(
        timing => timing.timingType === TimingType.ACTIVITY_QUALIFICATION
      );
      for (const timing of timings) {
        const matchedItem = timing.activityItems.find(
          item =>
            item.activityType === ActivityType.RECHARGE_ACTIVITY &&
            item.activityId === banner.activityGroupId
        );
        if (matchedItem) {
          const sort = matchedItem.sort ?? RECHARGE_PROMOTION_DEFAULT_PRIORITY;
          return sort * 100;
        }
      }

      return RECHARGE_PROMOTION_DEFAULT_PRIORITY;
    },
    [popupTimings]
  );

  const getBannerRemainingMs = useCallback((b: ActivityItem): number => {
    const remaining = Number(b?.userProgress?.countdownTime ?? NaN);
    return Number.isFinite(remaining) && remaining > 0 ? remaining : NaN;
  }, []);

  const shouldHideBannerTime = useCallback((b: ActivityItem): boolean => {
    const remaining = Number(b?.userProgress?.countdownTime ?? NaN);
    return !(Number.isFinite(remaining) && remaining > 0);
  }, []);

  const hasClaimableUnclaimed = useCallback((banner: ActivityItem): boolean => {
    return hasClaimableUnclaimedInternal(banner);
  }, []);

  const isAllClaimed = useCallback((banner: ActivityItem): boolean => {
    return isAllClaimedInternal(banner);
  }, []);

  const validBanners = useMemo(() => {
    // 获取 ACTIVITY_QUALIFICATION timing 中配置的 activityIds (groupIds)
    const timings = popupTimings.filter(
      timing => timing.timingType === TimingType.ACTIVITY_QUALIFICATION
    );
    const configuredGroupIds = new Set<string>();
    timings.forEach(timing => {
      timing.activityItems.forEach(item => {
        if (
          item.activityType === ActivityType.RECHARGE_ACTIVITY &&
          item.activityId
        ) {
          // item.activityId 就是 groupId
          configuredGroupIds.add(item.activityId);
        }
      });
    });
    return (rechargeActivityList || []).filter(
      b =>
        b &&
        !isAllClaimedInternal(b) &&
        b.activityGroupId &&
        configuredGroupIds.has(b.activityGroupId)
    );
  }, [rechargeActivityList, popupTimings]);
  const hasTriggeredClaimableRef = useRef(false);
  const lastClaimableActivityIdsRef = useRef<Set<string>>(new Set());
  const triggersMap = useRef<Map<string, () => void>>(new Map());

  // 注册trigger的回调
  const handleRegisterTrigger = useCallback(
    (activityId: string, trigger: () => void) => {
      triggersMap.current.set(activityId, trigger);
    },
    []
  );

  // 监听可领取状态变化，自动弹出弹窗
  useEffect(() => {
    if (!isConfigReady) return;
    if (!isAuthenticated || !userInfo?.userId) return;
    if (
      !Array.isArray(rechargeActivityList) ||
      rechargeActivityList.length === 0
    ) {
      return;
    }

    const claimableBanners = rechargeActivityList.filter(
      b => b && hasClaimableUnclaimed(b)
    );

    if (claimableBanners.length === 0) {
      hasTriggeredClaimableRef.current = false;
      lastClaimableActivityIdsRef.current.clear();
      return;
    }

    const userId = userInfo.userId;
    let shouldTrigger = false;
    let bannerToTrigger: ActivityItem | null = null;

    for (const banner of claimableBanners) {
      const activityId = banner.activityGroupId || banner.id || "";
      if (hasShownClaimablePopupToday(userId, activityId)) continue;
      if (lastClaimableActivityIdsRef.current.has(activityId)) continue;

      shouldTrigger = true;
      bannerToTrigger = banner;
      break;
    }

    if (shouldTrigger && bannerToTrigger) {
      const activityId =
        bannerToTrigger.activityGroupId || bannerToTrigger.id || "";
      markClaimablePopupShownToday(userId, activityId);
      lastClaimableActivityIdsRef.current.add(activityId);
      hasTriggeredClaimableRef.current = true;
      manualBannerRef.current = bannerToTrigger;
      const triggerFn = triggersMap.current.get(activityId);
      if (triggerFn) {
        triggerFn();
      }
    }
  }, [
    isConfigReady,
    rechargeActivityList,
    isAuthenticated,
    userInfo?.userId,
    hasClaimableUnclaimed,
  ]);

  const getTriggerByActivityId = useCallback(
    (activityId: string) => {
      const activity = (rechargeActivityList || []).find(
        b => b?.activityGroupId === activityId
      );
      if (!activity) return undefined;
      return () => {
        manualBannerRef.current = activity;
        const triggerFn = triggersMap.current.get(activityId);
        if (triggerFn) {
          triggerFn();
        }
      };
    },
    [rechargeActivityList]
  );

  // 创建弹窗注册组件
  const PopupRegistrationComponent = useCallback(() => {
    return (
      <AllActivityPopupsRegistration
        banners={validBanners}
        getActivityPriority={getActivityPriority}
        isConfigReady={isConfigReady}
        isAuthenticated={isAuthenticated}
        userInfo={userInfo}
        manualBannerRef={manualBannerRef}
        onRegisterTrigger={handleRegisterTrigger}
        activities={activities}
      />
    );
  }, [
    validBanners,
    getActivityPriority,
    isConfigReady,
    isAuthenticated,
    userInfo,
    handleRegisterTrigger,
    popupTimings,
    activities,
  ]);

  // 稳定化 banners 引用，避免下游组件无限重新渲染
  const stableBanners = useMemo(() => {
    return rechargeActivityList || [];
  }, [rechargeActivityList]);

  return {
    banners: stableBanners,
    getBannerRemainingMs,
    shouldHideBannerTime,
    hasClaimableUnclaimed,
    isAllClaimed,
    getTriggerByActivityId,
    PopupRegistrationComponent,
  };
};
